import {
  DevHubBox,
  DevHubLink,
  DevHubTypography,
  devhubMakeStyles,
} from '@devxdiscover/devhub-ui';

const useStyles = devhubMakeStyles((theme: any) => ({
  resourceSvgIcon: {
    marginRight: theme.spacing(1.5),
    width: 20,
    height: 20,
    display: 'inline-block',
  },
  resourceText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem', // Reduced from 0.95rem for better fit
    color: theme.palette.text.primary,
  },
  resourceBox: {
    display: 'flex',
    alignItems: 'center',
    padding: 8,
    marginBottom: 8,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.devhubBackground.workbox,
  },
}));

interface ResourceItemProps {
  /** Path to the resource icon */
  iconSrc: string;
  /** Alt text for the icon */
  iconAlt: string;
  /** The resource name or identifier */
  children: React.ReactNode;
  /** Optional URL to link to */
  href?: string;
  /** Additional custom content */
  extra?: React.ReactNode;
  /** Optional className for custom styling */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
}

/**
 * Consistent resource item display with icon and text/link
 * Used across IdeaCard, IdeaPreviewCard, and ReviewStep
 * @param props - Component props
 * @param props.iconSrc - Path to the resource icon
 * @param props.iconAlt - Alt text for the icon
 * @param props.children - The resource name or identifier
 * @param props.href - Optional URL to link to
 * @param props.extra - Additional custom content
 * @param props.className - Optional className for custom styling
 * @param props.style - Optional inline styles
 */
export const ResourceItem = ({
  iconSrc,
  iconAlt,
  children,
  href,
  extra,
  className,
  style,
}: ResourceItemProps) => {
  const classes = useStyles();

  return (
    <DevHubBox
      className={`${classes.resourceBox}${className ? ` ${className}` : ''}`}
      style={style}
    >
      <img src={iconSrc} alt={iconAlt} className={classes.resourceSvgIcon} />
      {href ? (
        <DevHubLink
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontFamily: 'monospace',
            fontSize: '0.8rem', // Reduced from 0.95rem for better fit
          }}
        >
          {children}
        </DevHubLink>
      ) : (
        <DevHubTypography className={classes.resourceText}>
          {children}
        </DevHubTypography>
      )}
      {extra}
    </DevHubBox>
  );
};
